/**
 * Defines the structure for navigation links used across different apps.
 * Each app (e.g., platform, stationapp) has an array of link objects.
 * Each link object can include:
 *  - label: Display name for the link
 *  - path: Route path for navigation
 *  - permissions: List of required permissions to view the link
 *  - icon: ng-icon name for the link
 */
export const navbarLinks = {
  platform: [
    {
      /** Links for the core platform module */
      label: 'Home', // visible without permissions
      path: '/platform/home',
      permissions: [],
      icon: 'matHome', // ng-icon name
    },
    {
      label: 'Users',
      path: '/platform/users',
      permissions: ['User.Read'], // requires read access to users
      icon: 'matPerson', // ng-icon name
    },
  ],
  /** Links for Mock App A */
  mockappa: [
    {
      label: 'Items',
      path: '/mockAppA/items',
      permissions: ['Item.Read'],
      icon: 'matList', // ng-icon name
    },
  ],
  /** Links for Mock App B */
  mockAppB: [
    {
      label: 'Objects',
      path: '/mockAppB/objects',
      permissions: ['Object.Read'],
      icon: 'matWidgets', // ng-icon name
    },
  ],
  /** Links for Mock App C */
  mockAppC: [
    {
      label: 'Transactions',
      path: '/mockAppC/transactions',
      permissions: ['Transaction.Read'],
      icon: 'matSwapHoriz', // ng-icon name
    },
  ],
  /** Links for the Station Status App */

  stationstatusapp: [
    {
      label: 'Stations',
      path: '/stationstatusapp/stations',
      permissions: ['Station.Read'],
      icon: 'matLocationOn', // ng-icon name
    },
  ],
  /** Links for the main Station App */

  stationapp: [
    {
      label: 'Home',
      path: '/stationapp/home',
      permissions: [],
      icon: 'matHome', // ng-icon name
    },
    {
      label: 'Make A Payment',
      path: '/stationapp/payment',
      permissions: [
        'Moneris.ProcessPayment', 'Consumer.Moneris.ProcessPayment',
        'Worldline.ProcessPayment', 'Consumer.Worldline.ProcessPayment',
        'PointAndPay.ProcessPayment', 'Consumer.PointAndPay.ProcessPayment',
      ],
      icon: 'matPayment', // ng-icon name
    },
    {
      label: 'Transactions',
      path: '/stationapp/transactions',
      permissions: ['Transaction.Read', 'Consumer.Transaction.Read'],
      icon: 'matSwapHoriz', // ng-icon name
    },
    {
      label: 'Access Codes',
      path: '/stationapp/consumeraccesstoken',
      permissions: ['Consumer.ConsumerAccessToken.Read'],
      icon: 'matVpnKey', // ng-icon name
      showForPermissionSetTypes: ['user'],
    },
    {
      label: 'Prepayments',
      path: '/stationapp/billing/prepayment',
      permissions: ['Consumer.Prepayment.Read'],
      icon: 'matAccountBalanceWallet', // ng-icon name
      showForPermissionSetTypes: ['user'],
      hideWhenFinancialDataHidden: true,
    },
    
    {
      label: 'Users',
      path: '/stationapp/users',
      permissions: ['Consumer.User.Read'],
      icon: 'matPerson', // ng-icon name
      superUserAccess: false,
      showForPermissionSetTypes: ['user'],
    },
    {
      label: 'Station Access',
      path: '/stationapp/stations',
      permissions: ['Consumer.Station.Read'],
      icon: 'matWaterDrop', // ng-icon name
      showForPermissionSetTypes: ['user'],
    },
    
    {
      label: 'Customers',
      icon: 'matGroup', // ng-icon name
      permissions: [],
      hideForPermissionSetTypes: ['user'],
      children: [
        {
          label: 'List',
          path: '/stationapp/consumers',
          permissions: ['Consumer.Read'],
          icon: 'matFormatListBulleted', // ng-icon name
        },
        {
          label: 'Access Codes',
          path: '/stationapp/consumeraccesstoken',
          permissions: ['ConsumerAccessToken.Read'],
          icon: 'matVpnKey', // ng-icon name
        },
        {
          label: 'Prepayments',
          path: '/stationapp/billing/prepayment',
          permissions: ['Prepayment.Read'],
          icon: 'matAccountBalanceWallet', // ng-icon name
          hideWhenFinancialDataHidden: true,
        },
        {
          label: 'Applications',
          path: '/stationapp/consumerapplications',
          permissions: ['ConsumerApplication.Read'],
          icon: 'matApps', // ng-icon name
        },
        {
          label: 'Logins',
          path: '/stationapp/users',
          permissions: ['Login.Read'],
          icon: 'matPerson', // ng-icon name
        },
      ]
    },
    {
      label: 'Billing',
      icon: 'matReceipt', // ng-icon name
      permissions: [],
      hideForPermissionSetTypes: ['user'],
      children: [
        {
          label: 'Batch Billing',
          path: '/stationapp/billing/batchbilling',
          permissions: ['Billing.Generate'],
          icon: 'matPlaylistAddCheck', // ng-icon name
        },
        {
          label: 'Invoices',
          path: '/stationapp/billing/creditpayment',
          permissions: ['CreditPayment.Read', 'Consumer.CreditPayment.Read'],
          icon: 'matReceiptLong', // ng-icon name
        },
        {
          label: 'Service Charges',
          path: '/stationapp/billing/servicecharges',
          permissions: ['ServiceCharge.Read'],
          icon: 'matAttachMoney', // ng-icon name
        },
      ]
    },
    {
      label: 'Setup',
      icon: 'matSettings', // ng-icon name
      permissions: [],
      hideForPermissionSetTypes: ['user'],
      children: [
        {
          label: 'Stations',
          path: '/stationapp/stations',
          permissions: ['Station.Read'],
          icon: 'matWaterDrop', // ng-icon name
        },
        
        {
          label: 'Customer Groups',
          path: '/stationapp/consumercategories',
          permissions: ['ConsumerCategory.Read'],
          icon: 'matCategory', // ng-icon name
        },
        {
          label: 'Payment Settings',
          path: '/stationapp/paymentconfiguration',
          permissions: ['PaymentConfiguration.Read'],
          superUserAccess: false,
          icon: 'matPayment', // ng-icon name
        },
        {
          label: 'Email All Customers',
          path: '/stationapp/bulkemail',
          permissions: ['BulkEmail.Read'],
          icon: 'matEmail', // ng-icon name
        },
      ]
    },
    {
      label: 'Reports',
      path: '/stationapp/reports',
      permissions: ['Report.Generate', 'Consumer.Report.Generate'],
      icon: 'matAssessment', // ng-icon name
      hideForCustomerWhenFinancialDataHidden: true,
    },
    {
      label: 'Admin Users',
      path: '/stationapp/adminusers',
      permissions: ['AdminUser.Read'],
      icon: 'matPerson', // ng-icon name
    },
  ],

  trashpassapp: [
    {
      label: 'Home',
      path: '/trashpassapp/home',
      permissions: [],
      icon: 'matHome', // ng-icon name
    },
    {
      label: 'Residents',
      path: '/trashpassapp/consumers',
      permissions: ['Resident.Read'],
      icon: 'matGroup', // ng-icon name
    },
    {
      label: 'RFID Cards',
      path: '/trashpassapp/consumeraccesstokens',
      permissions: ['RFIDCard.Read'],
      icon: 'matCreditCard',
    },
    {
      label: 'Tickets',
      path: '/trashpassapp/tickets',
      permissions: ['Ticket.Read'],
      icon: 'matConfirmationNumber', // ng-icon name
    },
    {
      label: 'Transfer Stations',
      path: '/trashpassapp/stations',
      permissions: ['TransferStation.Read'],
      icon: 'matLocationOn', // ng-icon name
    },
    {
      label: 'Resident Categories',
      path: '/trashpassapp/consumercategories',
      permissions: ['ResidentCategory.Read'],
      icon: 'matCategory', // ng-icon name
    },
    {
      label: 'Regions',
      path: '/trashpassapp/regions',
      permissions: ['Region.Read'],
      icon: 'matPlace', // ng-icon name
    },
    {
      label: 'Reports',
      path: '/trashpassapp/reports',
      permissions: ['Report.Generate'],
      icon: 'matAssessment', // ng-icon name
    }
  ],
  /** Links for the Flushing Station App */
  flushingstationapp: [
    {
      label: 'Dashboard',
      path: '/flushingstationapp/home',
      permissions: ['Dashboard', 'OperatorAdmin', 'OperatorUser'],
      icon: 'matDashboard', // ng-icon name
    },
    {
      label: 'Stations',
      path: '/flushingstationapp/stations',
      permissions: ['Station.Read', 'OperatorAdmin', 'OperatorUser'],
      icon: 'matWaterDrop', // ng-icon name
    },
    {
      label: 'Data Export',
      path: '/flushingstationapp/data-export',
      permissions: ['DataExport.Read', 'OperatorAdmin', 'OperatorUser'],
      icon: 'matDownload', // ng-icon name
    },
    {
      label: 'Users',
      path: '/flushingstationapp/users',
      permissions: ['User.Read', 'OperatorAdmin'],
      icon: 'matPerson', // ng-icon name
    },
    {
      label: 'Audit Log',
      path: '/flushingstationapp/audit-log',
      permissions: ['AuditLog.Read', 'OperatorAdmin', 'OperatorUser'],
      icon: 'matHistory', // ng-icon name
    },
  ],
};

/**
 * Maps route keys or segments to their human-readable breadcrumb labels.
 * Used to display meaningful names in the breadcrumb navigation component.
 * No Longer used but keep for future reference
 */
export const breadCrumbLinkNames: { [key: string]: string | undefined } = {
  mockappa: 'Mock App A',
  mockappb: 'Mock App B',
  mockappc: 'Mock App C',
  stationapp: 'Station App',
  stationstatusapp: 'Station Status App',
  trashpassapp: 'TrashPass App',
  servicecharges: 'Service Charges',
  consumercategories: 'Customer Groups',
  consumeraccesstoken: 'Access Codes',
  consumerapplications: 'Customer Applications',
  stations: 'Stations',
  stationtransactions: 'Transactions',
};
